import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, Shield, User, Search, Briefcase } from "lucide-react";
import { api, formatError } from "../../lib/api";

export default function EmployeesDirectory() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/employees"); setUsers(data); }
      catch (e) { toast.error(formatError(e)); }
    })();
  }, []);

  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    return !q ||
      u.name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.department?.toLowerCase().includes(q) ||
      u.role?.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6" data-testid="employees-directory-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-[#DC2A1C] font-semibold mb-1">Team</div>
        <h1 className="text-3xl font-black tracking-tight text-[#1A1A1A]">Employee Directory</h1>
        <p className="text-sm text-gray-500 mt-1">{users.length} team members</p>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email or department..."
          className="w-full bg-[#F8F9FA] border border-gray-200 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#DC2A1C]"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="employees-grid">
        {filtered.map((u) => (
          <div key={u.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow" data-testid={`employee-card-${u.id}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-full bg-[#DC2A1C] text-white grid place-items-center font-bold text-lg flex-shrink-0">
                {u.name?.slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-[#1A1A1A] truncate">{u.name}</div>
                <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold mt-0.5 ${u.role === "admin" ? "text-[#DC2A1C]" : "text-gray-500"}`}>
                  {u.role === "admin" ? <Shield className="w-3 h-3" /> : <User className="w-3 h-3" />} {u.role}
                </span>
              </div>
            </div>

            <div className="space-y-1.5 text-sm border-t border-gray-100 pt-3">
              {u.department && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Briefcase className="w-3.5 h-3.5 text-[#DC2A1C] flex-shrink-0" />
                  <span className="text-xs font-medium">{u.department}</span>
                </div>
              )}
              <a href={`mailto:${u.email}`} className="flex items-center gap-2 text-gray-700 hover:text-[#DC2A1C] hover:underline break-all" data-testid={`employee-email-${u.id}`}>
                <Mail className="w-3.5 h-3.5 text-[#DC2A1C] flex-shrink-0" /> {u.email}
              </a>
              {u.phone && (
                <a href={`tel:${u.phone.replace(/\s+/g, '')}`} className="flex items-center gap-2 text-gray-700 hover:text-[#DC2A1C] hover:underline" data-testid={`employee-phone-${u.id}`}>
                  <Phone className="w-3.5 h-3.5 text-[#DC2A1C] flex-shrink-0" /> {u.phone}
                </a>
              )}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-10 text-gray-500 text-sm">
            {search ? `No employees found for "${search}"` : "No employees yet."}
          </div>
        )}
      </div>
    </div>
  );
}
